/**
 * shadcn/ui Button — vendored + tokenized for CertAIn v2 (Phase 1a).
 *
 * Standard shadcn new-york Button (CVA variants + Radix Slot `asChild`), with
 * every stock `hsl(var(--…))` color swapped for CertAIn SEMANTIC Tailwind
 * utilities (bg-surface-*, text-text-*, border-border-*, ring-border-focus).
 * Those utilities resolve to the v2 navy/blue values inside AppShellV2's
 * `[data-theme="v2"]` scope, so no shadcn zinc leaks in and the no-raw-color
 * guard passes (zero hex / rgb / bg-[#…]).
 *
 * Brand treatment: the `default` (primary) variant uses the locked CTA gradient
 * (`bg-brand-grad`) with on-brand text — the brand asset wins over shadcn's
 * solid primary, per design/v2-direction/README.md.
 */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus focus-visible:ring-offset-2 focus-visible:ring-offset-surface-canvas disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Locked CTA gradient — the brand primary action (README §2, table).
        default:
          "bg-brand-grad text-text-on-brand shadow-sm hover:bg-brand-grad-hover",
        destructive:
          "bg-status-rejected-bg text-status-rejected-fg ring-1 ring-inset ring-status-rejected-ring hover:bg-surface-card-hover",
        outline:
          "border border-border-strong bg-transparent text-text-primary hover:bg-surface-card-hover",
        // Bordered fill so the secondary action stays clearly visible on ANY
        // surface — including card/canvas surfaces where the surface-subtle fill
        // alone is near-invisible (the v2 write-up-button "plain text" bug).
        secondary:
          "border border-border-strong bg-surface-subtle text-text-primary hover:bg-surface-card-hover hover:border-border-emphasis",
        ghost: "text-text-secondary hover:bg-surface-card-hover hover:text-text-primary",
        link: "text-text-link underline-offset-4 hover:text-text-link-hover hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-6",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
